import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/OrderHistory.css";

const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  // Function to format date from "2025-09-15T18:06:40.000Z" to "dd.mm.yyyy" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Calculate next payment date
  const calculateNextPayment = (createdAt, billing) => {
    const date = new Date(createdAt);
    if (billing === "month") {
      date.setMonth(date.getMonth() + 1);
    } else if (billing === "monthly") {
      date.setMonth(date.getMonth() + 1);
    } else if (billing === "year") {
      date.setFullYear(date.getFullYear() + 1);
    } else if (billing === "annually") {
      date.setFullYear(date.getFullYear() + 1);
    }
    return formatDate(date);
  };

  // Translate billing frequency
  const translateBilling = (billing) => {
    const billingMap = {
      monthly: "Månedlig",
      month: "Månedlig",
      year: "Årlig",
      annually: "Årlig",
    };
    return billingMap[billing] || billing;
  };

  // Determine subscription status based on cancel_at_period_end
  const getSubscriptionStatus = (cancelAtPeriodEnd) => {
    return cancelAtPeriodEnd === "1" ? "canceling" : "active";
  };

  // Translate subscription status for display
  const translateStatus = (cancelAtPeriodEnd) => {
    return cancelAtPeriodEnd === "1" ? "Avsluttes" : "Aktiv";
  };

  // Fetch subscriptions from the backend
  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);

      if (!accessToken || !userEmail) {
        return;
      }

      const response = await fetch(`${API_URL}/subscriptions`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSubscriptions(data.subscriptions || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Kunne ikke hente abonnementer. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, accessToken, userEmail]);

  // Cancel subscription function
  const cancelSubscription = async (subscriptionId) => {
    try {
      if (!accessToken || !userEmail) {
        throw new Error("Authentication not ready");
      }

      const response = await fetch(
        `${API_URL}/subscriptions/cancel/${subscriptionId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh the subscriptions list
      fetchSubscriptions();
      setShowCancelConfirm(false);
      setSelectedSubscription(null);
    } catch (err) {
      console.error("Error canceling subscription:", err);
      alert("Kunne ikke kansellere abonnement. Prøv igjen senere.");
    }
  };

  // Restore subscription function
  const restoreSubscription = async (subscriptionId) => {
    try {
      if (!accessToken || !userEmail) {
        throw new Error("Authentication not ready");
      }

      const response = await fetch(
        `${API_URL}/subscriptions/restore/${subscriptionId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh the subscriptions list
      fetchSubscriptions();
      setShowRestoreConfirm(false);
      setSelectedSubscription(null);
    } catch (err) {
      console.error("Error restoring subscription:", err);
      alert("Kunne ikke gjenopprette abonnement. Prøv igjen senere.");
    }
  };

  // Handle cancel button click
  const handleCancelClick = (subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelConfirm(true);
  };

  // Handle restore button click
  const handleRestoreClick = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRestoreConfirm(true);
  };

  // Handle cancel confirmation dialog response
  const handleCancelConfirmation = (confirmed) => {
    if (confirmed && selectedSubscription) {
      cancelSubscription(selectedSubscription.id);
    } else {
      setShowCancelConfirm(false);
      setSelectedSubscription(null);
    }
  };

  // Handle restore confirmation dialog response
  const handleRestoreConfirmation = (confirmed) => {
    if (confirmed && selectedSubscription) {
      restoreSubscription(selectedSubscription.id);
    } else {
      setShowRestoreConfirm(false);
      setSelectedSubscription(null);
    }
  };

  useEffect(() => {
    if (accessToken && userEmail) {
      fetchSubscriptions();
    }
  }, [accessToken, userEmail, fetchSubscriptions]);

  if (loading) {
    return (
      <div className="subscription-management">
        <h1>Abonnementer</h1>
        <div className="loading">Laster inn abonnementer...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-management">
        <h1>Abonnementer</h1>
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchSubscriptions} className="retry-btn">
            Prøv på nytt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-management">
      <h1>Abonnementer</h1>

      {showCancelConfirm && (
        <div className="confirmation-dialog">
          <div className="confirmation-content">
            <p>
              Er du sikker på at du vil kansellere abonnementet? Abonnementet
              vil være aktivt frem til neste betaling.
            </p>
            <div className="confirmation-buttons">
              <button
                onClick={() => handleCancelConfirmation(true)}
                className="confirm-btn"
              >
                Ja
              </button>
              <button
                onClick={() => handleCancelConfirmation(false)}
                className="cancel-btn"
              >
                Nei
              </button>
            </div>
          </div>
        </div>
      )}

      {showRestoreConfirm && selectedSubscription && (
        <div className="confirmation-dialog">
          <div className="confirmation-content">
            <p>
              Dette abonnementet blir ikke lenger kansellert. Det fornyes den{" "}
              {calculateNextPayment(
                selectedSubscription.created_at,
                selectedSubscription.billing
              )}
            </p>
            <div className="confirmation-buttons">
              <button
                onClick={() => handleRestoreConfirmation(true)}
                className="confirm-btn"
              >
                Forny abonnement
              </button>
              <button
                onClick={() => handleRestoreConfirmation(false)}
                className="cancel-btn"
              >
                Tilbake
              </button>
            </div>
          </div>
        </div>
      )}

      {subscriptions.length === 0 ? (
        <div className="no-subscriptions">
          <p>Du har ingen abonnementer ennå.</p>
        </div>
      ) : (
        <div className="subscriptions-table-container">
          <table className="subscriptions-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Pakke</th>
                <th>Dato opprettet</th>
                <th>Betalingsfrekvens</th>
                <th>Neste betaling</th>
                <th>Status</th>
                <th>Allokering</th>
                <th>Domene</th>
                <th>Handling</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => {
                const isCanceling = subscription.cancel_at_period_end === "1";
                return (
                  <tr key={subscription.id}>
                    <td className="subscription-id">#{subscription.id}</td>
                    <td className="subscription-title">{subscription.title}</td>
                    <td className="subscription-created">
                      {formatDate(subscription.created_at)}
                    </td>
                    <td className="subscription-billing">
                      {translateBilling(subscription.billing)}
                    </td>
                    <td className="subscription-next-payment">
                      {calculateNextPayment(
                        subscription.created_at,
                        subscription.billing
                      )}
                    </td>
                    <td className="subscription-status">
                      <span
                        className={`status-badge status-${getSubscriptionStatus(
                          subscription.cancel_at_period_end
                        )}`}
                      >
                        {translateStatus(subscription.cancel_at_period_end)}
                      </span>
                    </td>
                    <td className="subscription-allocated">
                      <span
                        className={`status-badge status-${
                          subscription.allocated ? "active" : "inactive"
                        }`}
                      >
                        {subscription.allocated ? "I bruk" : "Ikke i bruk"}
                      </span>
                    </td>
                    <td className="subscription-domain">
                      {subscription.domain || "Ingen domene"}
                    </td>
                    <td className="subscription-action">
                      {isCanceling ? (
                        <button
                          onClick={() => handleRestoreClick(subscription)}
                          className="restore-btn"
                        >
                          Angre kansellering
                        </button>
                      ) : (
                        <button
                          onClick={() => handleCancelClick(subscription)}
                          className="cancel-btn"
                        >
                          Kanseller abonnement
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
